import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, Navigate, useParams, useLocation, Outlet } from 'react-router';

import Layout from '@/components/templates/Layout';
import PermissionsWrapper from '@/components/templates/PermissionsWrapper';
import { setCampaignId } from '@/redux/session/actions';
import { selectAccountSlug } from '@/redux/session/selectors';

import { CampaignPerformance } from './components/performance/CampaignPerformance';
import { useInitializeCampaignRedux } from './hooks/useInitializeCampaignRedux';
import { CampaignsPage } from './pages/CampaignsPage';
import { UnifiedAnalytics } from './pages/unified/Analytics';
import { UnifiedAudience } from './pages/unified/Audience';
import { CampaignDetailLayout } from './pages/unified/CampaignDetailLayout';
import { CampaignWizardLayout } from './pages/unified/CampaignWizardLayout';
import { UnifiedLogs } from './pages/unified/Logs';
import { MessagingNBAConfig } from './pages/unified/MessagingNBAConfig';
import { UnifiedSubscriberJourney } from './pages/unified/SubscriberJourney';

/**
 * Maps a legacy type-specific sub-route segment to its unified equivalent.
 * Build/message(s) all collapsed into the single messaging step.
 */
const LEGACY_SEGMENT_MAP: Record<string, string> = {
  setup: 'setup',
  build: 'messaging',
  message: 'messaging',
  messages: 'messaging',
  review: 'review',
  audience: 'audience',
  analytics: 'analytics',
  logs: 'logs',
};

/**
 * Soft-lands legacy `outreach/*` and `payment-recovery/*` URLs (old bookmarks,
 * browser history, external links) on the unified flow instead of a blank page.
 * The v1 wizard routes were removed when the unified flow became the only flow.
 */
const LegacyCampaignRedirect: React.FC = () => {
  const { campaignId, '*': rest } = useParams<{ campaignId: string; '*': string }>();
  const { search } = useLocation();
  const slug = useSelector(selectAccountSlug);

  const [segment, subscriberId] = (rest ?? '').split('/');
  const base = LEGACY_SEGMENT_MAP[segment] ?? 'setup';
  const target = base === 'logs' && subscriberId ? `logs/${subscriberId}` : base;

  return <Navigate to={`/${slug}/campaigns/${campaignId}/${target}${search}`} replace />;
};

/**
 * Wrapper component that handles campaign ID setting and data loading
 * This runs once when entering any campaign route
 */
const CampaignRouteWrapper: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const dispatch = useDispatch();

  // Initialize campaign data in Redux store for existing campaigns
  useInitializeCampaignRedux();

  // Set campaign ID in Redux whenever the URL param changes
  useEffect(() => {
    if (campaignId) {
      dispatch(setCampaignId(campaignId));
    }
  }, [campaignId, dispatch]);

  return <Outlet />;
};

export const CampaignsRouter: React.FC = () => {
  return (
    <Layout>
      {/* TODO: Change to campaigns once we have the proper permissions */}
      <PermissionsWrapper module="campaigns">
        <Routes>
          {/* Campaigns listing page */}
          <Route index element={<CampaignsPage />} />

          {/* Individual campaign routes wrapped with CampaignRouteWrapper.
              All entry points (`CampaignsPage`, `EmptyCampaignsPage`) create
              the campaign immediately, so there's no draft URL state. */}
          <Route path=":campaignId" element={<CampaignRouteWrapper />}>
            <Route index element={<Navigate to="setup" replace />} />

            {/* setup/messaging/review share ONE mounted wizard so steps animate.
                The leaf elements are intentionally `null`: a matched leaf with a
                falsy element renders nothing, and the layout renders the step
                bodies via <StepWizard>. The markers exist only so the URLs keep
                matching the (stable, pathless) layout route. */}
            <Route element={<CampaignWizardLayout />}>
              <Route path="setup" element={null} />
              <Route path="messaging" element={null} />
              <Route path="review" element={null} />
            </Route>
            {/* NOT a wizard step — full-page NBA message editor; stays a sibling
                outside the layout so the wizard never tries to animate it. */}
            <Route path="messaging/configure" element={<MessagingNBAConfig />} />
            <Route element={<CampaignDetailLayout />}>
              <Route path="analytics" element={<UnifiedAnalytics />} />
              <Route path="performance" element={<CampaignPerformance />} />
              <Route path="logs" element={<UnifiedLogs />} />
            </Route>
            <Route path="logs/:subscriberId" element={<UnifiedSubscriberJourney />} />
            <Route path="audience" element={<UnifiedAudience />} />

            {/* Legacy type-specific routes → unified flow (no blank dead-ends) */}
            <Route path="outreach/*" element={<LegacyCampaignRedirect />} />
            <Route path="payment-recovery/*" element={<LegacyCampaignRedirect />} />
          </Route>
        </Routes>
      </PermissionsWrapper>
    </Layout>
  );
};
